/**
 * MCP Developer Tools — v5.9 "Developer Portal"
 *
 * 6 tools: get_api_reference, serve_preview, get_workspace_info,
 * inspect_trace_waterfall, get_dev_dashboard_state,
 * holo_generate_bindings
 *
 * @version 1.1.0
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import { APIDocsGenerator } from './api-docs-generator';
import { TraceWaterfallRenderer } from '@holoscript/core';
import {
  InteropBindingGenerator,
  type GeneratedBinding,
} from '../../core/src/interop/InteropBindingGenerator.js';
import type { TraceSpan } from '@holoscript/core';

// =============================================================================
// SINGLETONS
// =============================================================================

let docsGenerator: APIDocsGenerator | null = null;
let waterfallRenderer: TraceWaterfallRenderer | null = null;

function getDocsGenerator(): APIDocsGenerator {
  if (!docsGenerator) docsGenerator = new APIDocsGenerator({ version: '6.0.0' });
  return docsGenerator;
}

function getWaterfallRenderer(): TraceWaterfallRenderer {
  if (!waterfallRenderer) waterfallRenderer = new TraceWaterfallRenderer();
  return waterfallRenderer;
}

let bindingGenerator: InteropBindingGenerator | null = null;

function getBindingGenerator(): InteropBindingGenerator {
  if (!bindingGenerator) bindingGenerator = new InteropBindingGenerator();
  return bindingGenerator;
}

export function resetDeveloperSingletons(): void {
  docsGenerator = null;
  waterfallRenderer = null;
  bindingGenerator = null;
}

// =============================================================================
// TOOL DEFINITIONS
// =============================================================================

export const developerTools: Tool[] = [
  {
    name: 'get_api_reference',
    description:
      'Generate API reference documentation for all MCP tools. Returns markdown or JSON format with categories, parameters, and examples.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        format: {
          type: 'string',
          description: 'Output format: "markdown" or "json"',
          enum: ['markdown', 'json'],
        },
        category: {
          type: 'string',
          description: 'Filter by category name (optional)',
        },
      },
      required: [],
    },
  },
  {
    name: 'serve_preview',
    description:
      'Get dev server state and composition preview data. Returns current files, parse status, and connected client info.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        root: {
          type: 'string',
          description: 'Root directory to scan for compositions (defaults to working directory)',
        },
        extensions: {
          type: 'array',
          description: 'File extensions to include',
          items: { type: 'string' },
        },
      },
      required: [],
    },
  },
  {
    name: 'get_workspace_info',
    description:
      'Get workspace configuration, members, composition counts, and build order from holoscript.workspace.json.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        root: {
          type: 'string',
          description: 'Workspace root directory (defaults to working directory)',
        },
        includeBuildOrder: {
          type: 'boolean',
          description: 'Whether to include build order analysis (default: true)',
        },
      },
      required: [],
    },
  },
  {
    name: 'inspect_trace_waterfall',
    description:
      'Convert trace spans into a waterfall visualization with timing, nesting, agent colors, and critical path analysis.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        spans: {
          type: 'array',
          description: 'Array of TraceSpan objects to visualize',
          items: {
            type: 'object',
            properties: {
              traceId: { type: 'string' },
              spanId: { type: 'string' },
              parentSpanId: { type: 'string' },
              name: { type: 'string' },
              kind: { type: 'string' },
              startTime: { type: 'number' },
              endTime: { type: 'number' },
              status: { type: 'string' },
              attributes: { type: 'object' },
            },
          },
        },
        minDuration: {
          type: 'number',
          description: 'Minimum span duration to display (ms)',
        },
      },
      required: ['spans'],
    },
  },
  {
    name: 'holo_generate_bindings',
    description:
      'Generate Python or JavaScript/TypeScript bindings for a HoloScript (.hsplus) module. ' +
      'Returns generated binding code that lets external Python or JS callers import and invoke ' +
      'HoloScript compositions without knowing the TypeScript internals. ' +
      'Supports D.038 developer-enabled revival — any developer can reinnovate on HoloScript from their native language.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        modulePath: {
          type: 'string',
          description:
            'Path to the .hsplus source file to generate bindings for (absolute or workspace-relative)',
        },
        targetLang: {
          type: 'string',
          description: 'Target language for the generated bindings',
          enum: ['python', 'javascript', 'typescript'],
        },
        sourceCode: {
          type: 'string',
          description:
            'HoloScript+ source code as a string (alternative to modulePath — provide one or the other). ' +
            'Useful when the caller already has the source in memory.',
        },
      },
      required: ['targetLang'],
    },
  },
  {
    name: 'get_dev_dashboard_state',
    description:
      'Get comprehensive developer dashboard state including composition status, trace summaries, agent registry, plugin status, and budget/usage info.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        sections: {
          type: 'array',
          description:
            'Which dashboard sections to include (default: all). Options: compositions, traces, agents, plugins, economy, api',
          items: {
            type: 'string',
            enum: ['compositions', 'traces', 'agents', 'plugins', 'economy', 'api'],
          },
        },
      },
      required: [],
    },
  },
];

// =============================================================================
// HANDLER
// =============================================================================

export async function handleDeveloperTool(
  name: string,
  args: Record<string, unknown>
): Promise<unknown> {
  switch (name) {
    case 'get_api_reference':
      return handleGetApiReference(args);
    case 'serve_preview':
      return handleServePreview(args);
    case 'get_workspace_info':
      return handleGetWorkspaceInfo(args);
    case 'inspect_trace_waterfall':
      return handleInspectTraceWaterfall(args);
    case 'get_dev_dashboard_state':
      return handleGetDevDashboardState(args);
    case 'holo_generate_bindings':
      return handleGenerateBindings(args);
    default:
      throw new Error(`Unknown developer tool: ${name}`);
  }
}

// =============================================================================
// HANDLERS
// =============================================================================

async function loadToolsCatalog(): Promise<Tool[]> {
  // `tools.ts` imports this module; a dynamic import can resolve before `export const tools`
  // is assigned. Yield and retry briefly so the circular init can complete.
  for (let attempt = 0; attempt < 20; attempt++) {
    await new Promise<void>((resolve) => setImmediate(resolve));
    const mod = await import('./tools');
    const catalog = mod.tools;
    if (Array.isArray(catalog) && catalog.length > 0) return catalog;
  }
  throw new Error('MCP tools catalog failed to initialize');
}

async function handleGetApiReference(args: Record<string, unknown>): Promise<unknown> {
  const tools = await loadToolsCatalog();
  const generator = getDocsGenerator();
  const ref = generator.generate(tools);

  // Filter by category if specified
  if (args.category) {
    ref.categories = ref.categories.filter(
      (c) => c.name.toLowerCase() === (args.category as string).toLowerCase()
    );
    ref.totalTools = ref.categories.reduce((sum, c) => sum + c.tools.length, 0);
  }

  const format = (args.format as string) || 'json';
  if (format === 'markdown') {
    return { format: 'markdown', content: generator.toMarkdown(ref) };
  }

  return { format: 'json', reference: ref };
}

async function handleServePreview(args: Record<string, unknown>): Promise<unknown> {
  const { existsSync, readdirSync, readFileSync, statSync } = await import('fs');
  const { join, extname } = await import('path');

  const root = (args.root as string) || process.cwd();
  const extensions = (args.extensions as string[]) || ['.holo', '.hs', '.hsplus'];

  // Scan for compositions
  const compositions: Array<{
    path: string;
    size: number;
    lines: number;
  }> = [];

  const scan = (dir: string, depth: number): void => {
    if (depth <= 0) return;
    try {
      const entries = readdirSync(dir, { withFileTypes: true });
      for (const entry of entries) {
        const fullPath = join(dir, entry.name);
        if (entry.isDirectory() && !entry.name.startsWith('.') && entry.name !== 'node_modules') {
          scan(fullPath, depth - 1);
        } else if (entry.isFile() && extensions.includes(extname(entry.name))) {
          const stat = statSync(fullPath);
          const content = readFileSync(fullPath, 'utf-8');
          compositions.push({
            path: fullPath.replace(root, '').replace(/\\/g, '/'),
            size: stat.size,
            lines: content.split('\n').length,
          });
        }
      }
    } catch {
      // Skip unreadable
    }
  };

  if (existsSync(root)) {
    scan(root, 5);
  }

  return {
    root,
    compositionCount: compositions.length,
    compositions,
    extensions,
    serverRunning: false, // No live server in MCP context
  };
}

async function handleGetWorkspaceInfo(args: Record<string, unknown>): Promise<unknown> {
  const root = (args.root as string) || process.cwd();
  const includeBuildOrder = args.includeBuildOrder !== false;

  const { WorkspaceManager } = await import('./workspace-manager-bridge');

  const manager = new WorkspaceManager(root);
  const config = manager.load();

  if (!config) {
    return {
      found: false,
      root,
      message: 'No holoscript.workspace.json found. Use `holoscript workspace init` to create one.',
    };
  }

  const info = manager.getInfo();
  const result: Record<string, unknown> = {
    found: true,
    ...info,
  };

  if (includeBuildOrder) {
    result.buildOrder = manager.getBuildOrder();
  }

  return result;
}

async function handleInspectTraceWaterfall(args: Record<string, unknown>): Promise<unknown> {
  const spans = args.spans as TraceSpan[];
  if (!spans || !Array.isArray(spans)) {
    throw new Error('spans parameter is required and must be an array');
  }

  // Normalize span data — accept both flat (traceId/spanId at top) and nested (context) formats
  const normalized: TraceSpan[] = spans.map((s: Record<string, unknown>) => {
    const flat = s as Record<string, unknown>;
    const ctx = (flat.context as Record<string, unknown>) || {};
    const traceId = (ctx.traceId as string) || (flat.traceId as string) || '';
    const spanId = (ctx.spanId as string) || (flat.spanId as string) || '';
    const parentSpanId = (ctx.parentSpanId as string) || (flat.parentSpanId as string) || undefined;
    const startTime = (flat.startTime as number) || 0;
    const endTime = (flat.endTime as number) || 0;
    return {
      id: (flat.id as string) || spanId,
      name: (flat.name as string) || 'unnamed',
      context: {
        traceId,
        spanId,
        parentSpanId,
        traceFlags: (ctx.traceFlags as number) || 0,
        baggage: (ctx.baggage as Record<string, string>) || {},
      },
      startTime,
      endTime,
      duration: endTime - startTime,
      status: (flat.status as string) || 'unset',
      statusMessage: flat.statusMessage as string | undefined,
      attributes: (flat.attributes as Record<string, unknown>) || {},
      events: (flat.events as Array<unknown>) || [],
      links: (flat.links as Array<unknown>) || [],
      kind: (flat.kind as string) || 'internal',
    } as TraceSpan;
  });

  const renderer = getWaterfallRenderer();
  if (args.minDuration) {
    const customRenderer = new (TraceWaterfallRenderer as any)({
      minDuration: args.minDuration as number,
    });
    return { waterfall: customRenderer.render(normalized) };
  }

  return { waterfall: renderer.render(normalized) };
}

async function handleGetDevDashboardState(args: Record<string, unknown>): Promise<unknown> {
  const sections = (args.sections as string[]) || [
    'compositions',
    'traces',
    'agents',
    'plugins',
    'economy',
    'api',
  ];

  const state: Record<string, unknown> = {};

  if (sections.includes('traces')) {
    try {
      const { getTelemetryCollector } = await import('@holoscript/core');
      const collector = getTelemetryCollector();
      const stats = collector.getStats();
      state.traces = {
        totalSpans: stats.totalSpans,
        activeSpans: stats.activeSpans,
        totalEvents: stats.totalEvents,
      };
    } catch {
      state.traces = { totalSpans: 0, activeSpans: 0, totalEvents: 0 };
    }
  }

  if (sections.includes('api')) {
    try {
      const tools = await loadToolsCatalog();
      const generator = getDocsGenerator();
      const ref = generator.generate(tools);
      state.api = {
        totalTools: tools.length,
        categories: ref.categories.length,
      };
    } catch {
      state.api = { totalTools: 0, categories: 0 };
    }
  }

  if (sections.includes('economy')) {
    try {
      const { handleEconomyTool } = await import('./economy-tools');
      const usage = await handleEconomyTool('get_usage_summary', { period: 'monthly' });
      state.economy = usage;
    } catch {
      state.economy = { period: 'monthly', summary: { totalCalls: 0 } };
    }
  }

  if (sections.includes('agents')) {
    try {
      const { handleTool } = await import('./handlers');
      const health = await handleTool('get_agent_health', {});
      state.agents = health;
    } catch {
      state.agents = { agents: [], totalRegistered: 0 };
    }
  }

  if (sections.includes('plugins')) {
    try {
      const { handleTool } = await import('./handlers');
      const plugins = await handleTool('list_plugins', {});
      state.plugins = plugins;
    } catch {
      state.plugins = { plugins: [], total: 0 };
    }
  }

  if (sections.includes('compositions')) {
    state.compositions = await handleServePreview({});
  }

  return { dashboard: state, sections, timestamp: new Date().toISOString() };
}

// =============================================================================
// holo_generate_bindings
// =============================================================================

async function handleGenerateBindings(args: Record<string, unknown>): Promise<unknown> {
  const targetLang = (args.targetLang as string | undefined) ?? 'python';
  const modulePath = (args.modulePath as string | undefined) ?? '';
  const sourceCode = (args.sourceCode as string | undefined) ?? '';

  if (!modulePath && !sourceCode) {
    return {
      error: 'Provide either modulePath (path to .hsplus file) or sourceCode (raw source string)',
    };
  }

  // Parse the source — use provided code or read from disk
  let rawSource = sourceCode;
  if (!rawSource && modulePath) {
    try {
      const fs = await import('node:fs/promises');
      rawSource = await fs.readFile(modulePath, 'utf-8');
    } catch (err) {
      return { error: `Could not read ${modulePath}: ${(err as Error).message}` };
    }
  }

  // Parse with @holoscript/core parser
  // We import HSPlusAST from the same source path as InteropBindingGenerator to
  // avoid type-identity mismatches between dist/index and src/types.
  type SrcAST = import('../../core/src/types/HoloScriptPlus.js').HSPlusAST;
  let ast: SrcAST;
  try {
    const { parse } = await import('@holoscript/core/parser');
    const result = parse(rawSource) as {
      success: boolean;
      errors: Array<{ line: number; column: number; message: string }>;
      ast?: unknown;
    };
    if (!result.success) {
      const msgs = result.errors
        .map(
          (e: { line: number; column: number; message: string }) =>
            `${e.line}:${e.column} ${e.message}`
        )
        .join('; ');
      return { error: `Parse errors: ${msgs}` };
    }
    ast = (result.ast ?? { body: [] }) as SrcAST;
  } catch (err) {
    return { error: `Parse failed: ${(err as Error).message}`, source: rawSource.slice(0, 200) };
  }

  const generator = getBindingGenerator();
  const sourceLabel = modulePath || '<inline>';

  try {
    let binding: GeneratedBinding;
    if (targetLang === 'python') {
      binding = generator.generatePythonBindings(ast, sourceLabel);
    } else {
      // javascript and typescript both go through generateJSBindings;
      // the generator honours language: 'typescript' in its metadata
      binding = generator.generateJSBindings(ast, sourceLabel);
      if (targetLang === 'typescript') {
        binding = { ...binding, language: 'typescript' as const };
      }
    }

    return {
      language: binding.language,
      code: binding.code,
      exports: binding.exports,
      exportCount: binding.exports.length,
      metadata: binding.metadata,
      usage:
        targetLang === 'python'
          ? `# Save as <module>.py, then: from <module> import *`
          : `// Save as <module>.mjs, then: import * from './<module>.mjs'`,
    };
  } catch (err) {
    return { error: `Binding generation failed: ${(err as Error).message}` };
  }
}
